import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { useDispatch } from 'react-redux';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import WelcomeVideo from '@assets/videos/intro.mp4';
import Poster from '@assets/videos/welcome-poster.jpg';
import { toggleBootcampModal } from '@redux/actions/modalAction';
import { nonNull } from '@helpers/non-null';
import { CrossIcon, PlayerControlBarPlayIcon } from '@icons';
import './WelcomeModal.less';

const videoJsOptions: VideoJsPlayerOptions = {
  autoplay: false,
  fluid: false,
  responsive: true
};

export type WelcomeModalProps = {
  onCloseModal: () => void;
  open: boolean;
};

export default function WelcomeModal({
  open,
  onCloseModal
}: WelcomeModalProps) {
  const dispatch = useDispatch();
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const playerRef = React.useRef<VideoJsPlayer>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = nonNull(videoRef.current);
    const player = videojs(videoElement, videoJsOptions, () => {
      setReady(true);
    });
    (playerRef.current as any) = player;
    return () => {
      player.dispose();
    };
  }, []);

  useEffect(() => {
    if (!playerRef.current || !ready) return;
    playerRef.current.poster(Poster);
    playerRef.current.src({
      type: 'video/mp4',
      src: `${WelcomeVideo}`
    });
  }, [ready]);

  useEffect(() => {
    if (!playerRef.current) return;
    const player = playerRef.current;
    const onEnded = () => {
      setPlayVideo(false);
    };
    player.on('ended', onEnded);

    return () => {
      player.off('ended', onEnded);
    };
  }, []);

  const onVideoPlayPause = useCallback((v: boolean) => {
    setPlayVideo(v);
    if (!videoRef.current) return;
    if (v) videoRef.current.play();
    else videoRef.current.pause();
  }, []);

  const onOpenBootcamp = useCallback(() => {
    onCloseModal();
    dispatch(
      toggleBootcampModal({
        visible: true
      })
    );
  }, [onCloseModal, dispatch]);

  return (
    <Modal
      open={open}
      footer={null}
      centered={true}
      closeIcon={<CrossIcon />}
      destroyOnClose
      width={658}
      maskClosable={false}
      onCancel={onCloseModal}
      className="welcome-modal welcome-modal--contributor"
    >
      <div className="modal_container">
        <div className="welcome-modal_title">Welcome to StreamWork!</div>
        <div className="welcome-modal_content">
          <div
            className="welcome-modal_video"
            onClick={() => onVideoPlayPause(!playVideo)}
          >
            {!playVideo && (
              <div className="welcome-modal_video-start">
                <Button
                  icon={<PlayerControlBarPlayIcon />}
                  type="primary"
                  size="large"
                />
              </div>
            )}
            <div data-vjs-player>
              <video ref={videoRef} className="video-js" playsInline />
            </div>
          </div>
          <div className="welcome-modal_subtitle">
            Learn how to route for approval
          </div>
          <div className="welcome-modal_text">
            Watch a quick tutorial to see how easy it is
            <br />
            to route media for review in StreamWork.
          </div>
          <div className="welcome-modal_buttons">
            <Button
              onClick={onOpenBootcamp}
              type="primary"
              style={{ minWidth: 175 }}
            >
              Watch tutorial
            </Button>
            <Button
              type="ghost"
              onClick={onOpenBootcamp}
              style={{ minWidth: 189 }}
            >
              Open bootcamp
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
